import { CatalogueDataType } from 'src/lib/types/common/catalogue'

export type SelectionState = {
  selectedProducts: Record<string, CatalogueDataType>
  selectedVariants: Record<string, string[]> // productId -> variantIds
  isAllProductsSelected: boolean
}

export type SelectionAction =
  | { type: 'TOGGLE_PRODUCT'; product: CatalogueDataType; singleSelect?: boolean; disabledVariantIds?: Set<string> }
  | { type: 'TOGGLE_VARIANT'; product: CatalogueDataType; variant: CatalogueDataType; singleSelect?: boolean }
  | { type: 'TOGGLE_ALL'; checked: boolean }
  | { type: 'SET_SELECTION'; products: CatalogueDataType[] }

export const initialSelectionState: SelectionState = {
  selectedProducts: {},
  selectedVariants: {},
  isAllProductsSelected: false
}

/**
 * Reducer to manage the selection state of products and variants.
 * Handles the logic ensuring that:
 * 1. Selecting a product selects all its selectable variants, skipping disabled ones.
 * 2. Deselecting a product clears all its variants.
 * 3. Selecting a variant ensures the parent product is marked as selected.
 * 4. Deselecting use the last variant of a product also removes the product from selection.
 */
export function selectionReducer(state: SelectionState, action: SelectionAction): SelectionState {
  switch (action.type) {
    case 'TOGGLE_PRODUCT': {
      const { product, singleSelect, disabledVariantIds } = action
      if (!product.id) return state
      const productId = product.id as string
      const wasSelected = !!state.selectedProducts[productId]

      if (singleSelect) {
        // Only one product may ever be selected: picking a new one replaces any prior selection.
        if (wasSelected) {
          return { ...state, selectedProducts: {}, selectedVariants: {} }
        }
        const newVariants: Record<string, string[]> = {}
        // Selecting the product row picks its first variant by default; the user can
        // still switch to a different single variant via the variant list checkboxes.
        const firstVariantId = product.variants?.find(variant => !disabledVariantIds?.has(variant.id as string))?.id
        if (firstVariantId) {
          newVariants[productId] = [firstVariantId as string]
        }
        return { ...state, selectedProducts: { [productId]: product }, selectedVariants: newVariants }
      }

      // Create copies to ensure immutability
      const newProducts = { ...state.selectedProducts }
      const newVariants = { ...state.selectedVariants }

      if (wasSelected) {
        // If product is already selected, deselect it and remove all its variants
        delete newProducts[productId]
        delete newVariants[productId]
      } else {
        // If product is not selected, add it
        newProducts[productId] = product
        // Auto-select the variants that can be selected, leaving claimed ones out.
        if (product.variants?.length) {
          const selectable = product.variants
            .map(variant => variant.id as string)
            .filter(variantId => !disabledVariantIds?.has(variantId))

          if (!selectable.length) return state

          newVariants[productId] = selectable
        }
      }
      return { ...state, selectedProducts: newProducts, selectedVariants: newVariants }
    }

    case 'TOGGLE_VARIANT': {
      const { product, variant, singleSelect } = action
      if (!product.id || !variant.id) return state
      const productId = product.id as string
      const variantId = variant.id as string

      const isSwitchingProduct = singleSelect && !state.selectedProducts[productId]
      const newProducts = isSwitchingProduct ? {} : { ...state.selectedProducts }
      const newVariants = isSwitchingProduct ? {} : { ...state.selectedVariants }

      // Get currently selected variants for this product
      const currentSelectedVariants = newVariants[productId] || []
      const isVariantSelected = currentSelectedVariants.includes(variantId)

      if (isVariantSelected) {
        // Deselecting a variant
        const updatedVariants = currentSelectedVariants.filter(id => id !== variantId)

        if (updatedVariants.length === 0) {
          // If no variants left, remove the product entirely from selection
          delete newVariants[productId]
          delete newProducts[productId]
        } else {
          // Update the variants list for this product
          newVariants[productId] = updatedVariants
        }
      } else {
        // Selecting a variant: only one variant may be selected at a time in single-select mode
        const updatedVariants = singleSelect ? [variantId] : [...currentSelectedVariants, variantId]
        newVariants[productId] = updatedVariants

        // Ensure the parent product is in the selectedProducts map
        if (!newProducts[productId]) {
          newProducts[productId] = product
        }
      }
      return { ...state, selectedProducts: newProducts, selectedVariants: newVariants }
    }

    case 'TOGGLE_ALL':
      return { ...state, isAllProductsSelected: action.checked }

    case 'SET_SELECTION':
      return initializeSelectionState(action.products)

    default:
      return state
  }
}

export const initializeSelectionState = (products: CatalogueDataType[] = []): SelectionState => {
  const state: SelectionState = {
    selectedProducts: {},
    selectedVariants: {},
    isAllProductsSelected: false
  }

  products.forEach(product => {
    if (product.id === 'shopflo-all-products' || product.id === 'shopflo-all-collections') {
      state.isAllProductsSelected = true
      return
    }

    if (!product.id) return

    state.selectedProducts[product.id] = product

    if (product.variants && product.variants.length > 0) {
      state.selectedVariants[product.id] = product.variants.map(variant => variant.id as string)
    }
  })

  return state
}
